import { translationKey } from '../../../i18n';
import { localize, localizeSystemError } from '../../../i18n';
import { checkpointPermissionDraft } from '../../../utils/permissionDraft';
import SingleLineText from '../../../components/ui/SingleLineText';
import Button from '../../../components/ui/FitButton';
import AppButton from '../../../components/ui/AppButton';
import FeatureUpdateDialog from '../../../components/FeatureUpdateDialog';
import { needsAndroid105Update, needsIos105Update } from '../../../utils/release105Access';
import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { Box, Typography, Slider, Switch, FormControlLabel, Collapse, CircularProgress } from '@mui/material';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import VibrationIcon from '@mui/icons-material/Vibration';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import StopIcon from '@mui/icons-material/Stop';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AppToast from '../../../components/AppToast';
import AlertPermissionNotices from '../../../components/AlertPermissionNotices';
import { normalizeAlarmVolume, readAlertRuntimeInfo, readNativePlatform, supportsVibrationAlert } from '../../../utils/alertMethodSupport';

import useIosRingtoneMode from './useIosRingtoneMode';
import useAppLanguage from '../../../i18n';

const alertMethodCardSx = {
    px: 0.5, py: 1.7, minWidth: 0, minHeight: 116, borderRadius: 3,
    cursor: 'pointer', bgcolor: '#fff', textAlign: 'center', transition: 'all 0.2s ease',
    '&:active': { transform: 'scale(0.98)' },
};
const alertMethodTitleSx = {
    fontWeight: 700, fontSize: '1rem', color: '#1a1a1a', whiteSpace: 'nowrap',
};
const alertMethodSubtitleSx = {
    fontSize: '0.8rem', color: '#777', mt: 0.3, lineHeight: 1.4, whiteSpace: 'normal',
    overflowWrap: 'break-word',
};

/**
 * 알림 방식 sub-section — 등록/수정 확인 화면과 EditAlertMethodSheet에서 공통 사용.
 */
export default function AlertMethodSection({ form, setForm, initiallyShowWatchOptions = false }) {
    const { t } = useAppLanguage();
    const [testing, setTesting] = useState(false);
    const [watchOptionsOpen, setWatchOptionsOpen] = useState(initiallyShowWatchOptions);
    const watchOptionsId = useId();
    const [feedback, setFeedback] = useState(null);
    const [runtimeInfo, setRuntimeInfo] = useState(() => readAlertRuntimeInfo());
    const [nativePlatform, setNativePlatform] = useState(() => readNativePlatform());
    const testingRef = useRef(false);
    const testTimerRef = useRef(null);
    const volumeGestureRef = useRef({ startX: 0, startY: 0, mode: null });
    const canUseVibrationAlert = supportsVibrationAlert(runtimeInfo, nativePlatform);
    const needsIosModeUpdate = needsIos105Update(runtimeInfo);
    const needsVibrationUpdate = needsAndroid105Update(runtimeInfo);
    const [updateOpen, setUpdateOpen] = useState(false);
    const showVibrationOption = canUseVibrationAlert || needsVibrationUpdate;
    const supportsIosMode = !needsIosModeUpdate && !!runtimeInfo?.capabilities?.iosRingtoneMode;
    const { mode: iosMode, busy: modeBusy, error: modeError, statusError: modeStatusError, permissionHelp, requestedMode: requestedWatchMode, change: changeMode, query: queryMode } = useIosRingtoneMode(nativePlatform, supportsIosMode);
    const usesAlarmKit = nativePlatform === 'ios' && iosMode?.useAlarmKit;
    const canAdjustVolume = nativePlatform !== 'ios' || !supportsIosMode || iosMode?.useAlarmKit === false;
    const alarmPermissionMissing = iosMode?.authorized === false && (usesAlarmKit || permissionHelp);
    const needsAlarmPermission = !modeBusy && !modeStatusError && alarmPermissionMissing;
    const needsCriticalPermission = !modeBusy && !modeStatusError && iosMode?.useAlarmKit === false
        && iosMode?.criticalGranted === false && iosMode?.criticalSupported !== false && !needsAlarmPermission;
    const criticalUnavailable = !modeBusy && !modeStatusError && iosMode?.useAlarmKit === false
        && iosMode?.criticalSupported === false && !needsAlarmPermission;
    const showAlarmPermission = needsAlarmPermission && watchOptionsOpen;
    const watchStatus = modeBusy ? localize('적용 중')
        : needsAlarmPermission ? localize('권한 필요')
            : modeStatusError || modeError || iosMode?.pending ? localize('확인 필요')
                : usesAlarmKit && iosMode?.authorized === true ? localize('사용 중') : '';
    const openPermissionSettings = () => {
        checkpointPermissionDraft();
        window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'OPEN_IOS_NOTIFICATION_SETTINGS' }));
    };
    // Permission state, failed installation and unsupported builds need different next steps.
    // Opening Settings never changes the permission state here.
    const permissionGuide = modeStatusError ? {
        kind: 'status-error', title: localize('알림 설정 확인이 필요해요'),
        description: localize('현재 권한 상태를 다시 확인해 주세요.'), action: localize('다시 확인'), onAction: queryMode,
    } : !iosMode ? {
        kind: 'loading', title: localize('권한 확인 중'), action: localize('다시 확인'), onAction: queryMode,
    } : modeBusy ? (watchOptionsOpen ? {
        kind: 'applying', title: localize('설정 적용 중'), description: localize('잠시만 기다려 주세요.'),
    } : null) : showAlarmPermission ? {
        kind: 'alarm-needed', title: localize('알람 권한이 필요해요'),
        description: localize('설정한 시간에 벨소리 알람을 울려요.'),
        instruction: localize('설정에서 ‘알람’을 켜고 앱으로 돌아와 주세요.'),
        returnHint: usesAlarmKit ? null : localize('허용 후 위 스위치를 켜 주세요.'),
        action: localize('설정 열기'), onAction: openPermissionSettings,
    } : needsCriticalPermission ? {
        kind: 'critical-needed', title: localize('긴급 알림 권한이 필요해요'),
        description: localize('무음 모드에서도 복약 알림 소리를 내요.'),
        instruction: localize('알림 → ‘알림 허용’·‘긴급 알림’ 켜기'),
        action: localize('설정 열기'), onAction: openPermissionSettings,
    } : criticalUnavailable ? {
        kind: 'unsupported', title: localize('긴급 알림 사용 불가'),
        description: iosMode.selectable ? localize('벨소리는 위 워치 옵션을 켜서 사용해 주세요.') : localize('일반 알림을 선택해 주세요.'),
    } : !needsAlarmPermission && (watchOptionsOpen || !iosMode.selectable) && (iosMode.pending || modeError) ? {
        kind: 'apply-error', title: modeError ? localize('설정을 적용하지 못했어요') : localize('설정 적용을 확인해 주세요'),
        description: localize('잠시 후 다시 시도해 주세요.'),
        ...(iosMode.selectable && watchOptionsOpen ? {
            action: localize('다시 시도'), onAction: () => changeMode(requestedWatchMode ?? !!iosMode.useAlarmKit),
        } : {}),
    } : null;
    const showModeScope = !!iosMode && !modeBusy && !modeStatusError && !modeError && !iosMode.pending
        && !needsAlarmPermission && !needsCriticalPermission && !criticalUnavailable;

    const stopTestAlarm = useCallback(() => {
        if (testTimerRef.current) {
            clearTimeout(testTimerRef.current);
            testTimerRef.current = null;
        }
        if (testingRef.current && window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'STOP_TEST_ALARM' }));
        }
        testingRef.current = false;
        setTesting(false);
    }, []);

    useEffect(() => {
        const handler = (e) => {
            try {
                const msg = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
                if (msg?.type === 'TEST_ALARM_FEEDBACK') setFeedback(msg.message);
            } catch (_) {}
        };
        window.addEventListener('message', handler);
        document.addEventListener('message', handler);
        return () => {
            window.removeEventListener('message', handler);
            document.removeEventListener('message', handler);
        };
    }, []);

    useEffect(() => {
        return () => stopTestAlarm();
    }, [stopTestAlarm]);

    useEffect(() => {
        if (form.alertType !== 'FULLSCREEN' && form.alertType !== 'VIBRATION') stopTestAlarm();
    }, [form.alertType, stopTestAlarm]);

    useEffect(() => {
        const handler = (event) => {
            const nextRuntimeInfo = event?.detail || readAlertRuntimeInfo();
            setRuntimeInfo(nextRuntimeInfo);
            setNativePlatform(nextRuntimeInfo?.platform || readNativePlatform());
        };
        window.addEventListener('app-runtime-info-updated', handler);
        return () => window.removeEventListener('app-runtime-info-updated', handler);
    }, []);

    useEffect(() => {
        if (form.alertType === 'VIBRATION' && !canUseVibrationAlert) {
            setForm(prev => ({ ...prev, alertType: 'PUSH' }));
        }
    }, [form.alertType, setForm, canUseVibrationAlert]);

    useEffect(() => {
        if (form.alertType === 'FULLSCREEN' && normalizeAlarmVolume(form.alarmVolume) !== form.alarmVolume) {
            setForm(prev => ({ ...prev, alarmVolume: normalizeAlarmVolume(prev.alarmVolume) }));
        }
    }, [form.alertType, form.alarmVolume, setForm]);

    const testAlarm = () => {
        if (form.alertType === 'VIBRATION' && !canUseVibrationAlert) {
            if (needsVibrationUpdate) setUpdateOpen(true);
            return;
        }
        if (testingRef.current) {
            stopTestAlarm();
        } else {
            if (window.ReactNativeWebView) {
                checkpointPermissionDraft();
                window.ReactNativeWebView.postMessage(JSON.stringify({
                    type: form.alertType === 'VIBRATION' ? 'TEST_VIBRATION' : 'TEST_ALARM',
                    data: { volume: form.alarmVolume / 100 },
                }));
            } else {
                // 일반 브라우저(개발/웹뷰 외) — 실제 알람은 안 울리지만 UI 동작은 노출.
                setFeedback(form.alertType === 'VIBRATION'
                    ? translationKey('Android 앱에서만 실제 진동이 울려요. 미리보기 중이에요.')
                    : translationKey('앱에서만 실제 벨소리가 울려요. 미리보기 중이에요.'));
            }
            testingRef.current = true;
            setTesting(true);
            testTimerRef.current = setTimeout(() => {
                testTimerRef.current = null;
                testingRef.current = false;
                setTesting(false);
            }, 15000);
        }
    };

    const isPush = form.alertType === 'PUSH';
    const isVibration = form.alertType === 'VIBRATION';
    const isFullscreen = form.alertType === 'FULLSCREEN';
    const requestFullScreenIntentPermission = useCallback((alertType) => {
        if (nativePlatform !== 'android' || !window.ReactNativeWebView) return;
        window.dispatchEvent(new CustomEvent('full-screen-intent-permission-needed', {
            detail: {
                alertType,
                source: 'alert_method_selected',
            },
        }));
    }, [nativePlatform]);
    const selectAlertType = useCallback((nextAlertType) => {
        if (nextAlertType === 'VIBRATION' && !canUseVibrationAlert) {
            if (needsVibrationUpdate) setUpdateOpen(true);
            return;
        }
        setForm(prev => ({ ...prev, alertType: nextAlertType }));
        if (window.ReactNativeWebView) {
            checkpointPermissionDraft();
            window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'REQUEST_NOTIFICATION_PERMISSION' }));
        }
        if (nextAlertType === 'FULLSCREEN' || nextAlertType === 'VIBRATION') {
            requestFullScreenIntentPermission(nextAlertType);
        }
    }, [requestFullScreenIntentPermission, setForm, canUseVibrationAlert, needsVibrationUpdate]);
    const rememberVolumeTouchStart = (event) => {
        const point = event.touches?.[0] || event.changedTouches?.[0] || event;
        volumeGestureRef.current = {
            startX: point.clientX || 0,
            startY: point.clientY || 0,
            mode: null,
        };
    };
    const resetVolumeTouch = () => {
        volumeGestureRef.current = { startX: 0, startY: 0, mode: null };
    };
    const shouldApplyVolumeChange = (event) => {
        const nativeEvent = event?.nativeEvent || event;
        const isTouchLike = nativeEvent?.type?.startsWith('touch') || nativeEvent?.pointerType === 'touch';
        if (!isTouchLike) return true;

        const point = nativeEvent.touches?.[0] || nativeEvent.changedTouches?.[0] || nativeEvent;
        const { startX, startY, mode } = volumeGestureRef.current;
        const dx = Math.abs((point.clientX || 0) - startX);
        const dy = Math.abs((point.clientY || 0) - startY);

        if (!mode && (dx > 6 || dy > 6)) {
            volumeGestureRef.current.mode = dx > dy ? 'horizontal' : 'vertical';
        }
        return volumeGestureRef.current.mode === 'horizontal';
    };

    return (
        <Box>
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: showVibrationOption ? 'repeat(3, minmax(0, 1fr))' : '1fr 1fr',
                gap: 1,
                mb: (isFullscreen || isVibration) ? 2.2 : 0,
            }}>
                <Box
                    onClick={() => selectAlertType('PUSH')}
                    role="button"
                    tabIndex={0}
                    sx={{
                        ...alertMethodCardSx,
                        border: isPush ? '2px solid #ffe566' : '2px solid #e5e7eb',
                    }}
                >
                    <NotificationsOutlinedIcon sx={{ fontSize: 29, color: '#5a4a00', mb: 0.45 }} />
                    <SingleLineText sx={alertMethodTitleSx}>
                        {t('알림')}
                    </SingleLineText>
                    <SingleLineText sx={alertMethodSubtitleSx}>
                        {t('일반 푸시 알림')}
                    </SingleLineText>
                </Box>
                {showVibrationOption && (
                    <Box
                        onClick={() => selectAlertType('VIBRATION')}
                        role="button"
                        tabIndex={0}
                        sx={{
                            ...alertMethodCardSx,
                            border: isVibration ? '2px solid #ffe566' : '2px solid #e5e7eb',
                        }}
                    >
                        <VibrationIcon sx={{ fontSize: 29, color: '#5a4a00', mb: 0.45 }} />
                        <SingleLineText sx={alertMethodTitleSx}>
                            {t('진동 알림')}
                        </SingleLineText>
                        <SingleLineText sx={alertMethodSubtitleSx}>
                            {t('계속 진동해요')}
                        </SingleLineText>
                    </Box>
                )}
                <Box
                    onClick={() => selectAlertType('FULLSCREEN')}
                    role="button"
                    tabIndex={0}
                    sx={{
                        ...alertMethodCardSx,
                        border: isFullscreen ? '2px solid #ffe566' : '2px solid #e5e7eb',
                    }}
                >
                    <NotificationsActiveOutlinedIcon sx={{ fontSize: 29, color: '#5a4a00', mb: 0.45 }} />
                    <SingleLineText sx={alertMethodTitleSx}>
                        {t('벨소리 알람')}
                    </SingleLineText>
                    <SingleLineText sx={alertMethodSubtitleSx}>
                        {t('무음에서도 울려요')}
                    </SingleLineText>
                </Box>
            </Box>

            <AlertPermissionNotices medications={[form]} includeIos={false} />

            {isVibration && canUseVibrationAlert && (
                <Button
                    fullWidth
                    startIcon={testing ? <StopIcon /> : <VibrationIcon />}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={testAlarm}
                    sx={{
                        bgcolor: '#fff', color: testing ? '#ef4444' : '#5a4a00',
                        border: testing ? '1px solid #ef4444' : '1px solid #e5e7eb',
                        borderRadius: 3, py: 1.15, fontWeight: 700, fontSize: '0.95rem',
                        '&:hover': { bgcolor: '#f9fafb' },
                    }}
                >
                    {testing ? localize("정지") : localize("진동 테스트")}
                </Button>
            )}

            {isFullscreen && (
                <Box sx={{ p: 2.15, borderRadius: 3, bgcolor: '#fff', border: '1px solid #e5e7eb' }}>
                    {needsIosModeUpdate && <Button fullWidth variant="text"
                        onClick={() => setUpdateOpen(true)}
                        endIcon={<ExpandMoreIcon />}
                        sx={{ justifyContent: 'space-between', color: '#666', px: 0, py: 1.25, fontSize: '0.85rem', textAlign: 'left' }}>{localize("애플워치를 착용하시나요?")}</Button>}
                    {nativePlatform === 'ios' && supportsIosMode && (
                        <Box sx={{ mb: 2 }}>
                            {iosMode?.selectable && <>
                                <Button
                                    fullWidth
                                    variant="text"
                                    aria-expanded={watchOptionsOpen}
                                    aria-controls={watchOptionsId}
                                    onClick={() => setWatchOptionsOpen(open => !open)}
                                    endIcon={<ExpandMoreIcon sx={{ transform: watchOptionsOpen ? 'rotate(180deg)' : 'none', transition: 'transform 180ms' }} />}
                                    sx={{ justifyContent: 'space-between', color: '#666', px: 0, py: 1.25, fontSize: '0.85rem', fontWeight: 600, textAlign: 'left', '&:hover': { bgcolor: 'transparent' } }}
                                >
                                    <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>{localize("애플워치를 착용하시나요?")}{watchStatus && <SingleLineText component="span" role="status" sx={{ fontSize: '0.75rem', color: '#8a7000' }}>{localize(watchStatus)}</SingleLineText>}
                                    </Box>
                                </Button>
                                <Collapse in={watchOptionsOpen} timeout={180} unmountOnExit>
                                    <Box id={watchOptionsId} sx={{ pt: 0.5, pb: 0.5, containerType: 'inline-size' }}>
                                        <FormControlLabel
                                            labelPlacement="start"
                                            sx={{ m: 0, width: '100%', justifyContent: 'space-between', gap: 0.5, '& .MuiFormControlLabel-label': { minWidth: 0 }, '& .MuiSwitch-root': { flexShrink: 0 } }}
                                            control={<Switch size="small" disableRipple checked={modeBusy && typeof requestedWatchMode === 'boolean' ? requestedWatchMode : !!iosMode.useAlarmKit} disabled={modeBusy || testing || !!modeStatusError}
                                                onChange={(_, checked) => changeMode(checked)} />}
                                            label={<SingleLineText sx={{ fontSize: 'min(0.9rem, 4.6cqi)', fontWeight: 700, lineHeight: 1.5, whiteSpace: 'nowrap', letterSpacing: '-0.03em' }}>{localize("워치를 차도 폰에서 벨소리 울리기")}</SingleLineText>} />
                                        {watchStatus === localize('사용 중') && <Box sx={{ mt: 1, color: '#666', '& .MuiTypography-root': {
                                            fontSize: 'min(0.85rem, 3.65cqi)', lineHeight: 1.8, whiteSpace: 'nowrap', letterSpacing: '-0.03em',
                                        } }}>
                                            <Typography data-ios-volume-hint>{localize("음량은 폰 설정을 따르며, 직접 끌 때까지 계속 울려요.")}</Typography>
                                            <SingleLineText data-ios-alarm-usage>{localize("워치에서도 끄거나 ‘복용완료’를 누를 수 있어요.")}</SingleLineText>
                                        </Box>}
                                        {showModeScope && <Box sx={{ mt: 1, display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                            <SingleLineText data-ios-mode-scope sx={{ fontSize: '0.75rem', color: '#777', lineHeight: 1.6 }}>{localize("이 폰의 모든 벨소리 알람에 바로 적용돼요.")}</SingleLineText>
                                        </Box>}
                                    </Box>
                                </Collapse>
                            </>}
                            {permissionGuide && <Box role="status" data-ios-permission={permissionGuide.kind}
                                sx={{ mt: iosMode?.selectable ? 1 : 0, pt: iosMode?.selectable ? 2 : 0,
                                    borderTop: iosMode?.selectable ? '1px solid #eef0f2' : 'none' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                                    {['loading', 'applying'].includes(permissionGuide.kind)
                                        ? <CircularProgress size={18} sx={{ color: '#8a7000', flexShrink: 0 }} />
                                        : <LockOutlinedIcon sx={{ fontSize: 20, color: '#8a7000', flexShrink: 0 }} />}
                                    <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#24272b' }}>
                                        {permissionGuide.title}
                                    </SingleLineText>
                                </Box>
                                {permissionGuide.description && <Typography sx={{ fontSize: '0.85rem', color: '#636a73', lineHeight: 1.6, wordBreak: 'keep-all' }}>
                                    {permissionGuide.description}
                                </Typography>}
                                {permissionGuide.action && <AppButton fullWidth
                                    tone={permissionGuide.action === localize('설정 열기') ? 'primary' : 'secondary'}
                                    startIcon={permissionGuide.action === localize('설정 열기') ? <SettingsOutlinedIcon /> : <RefreshRoundedIcon />}
                                    sx={{ mt: 1.5, fontSize: '0.95rem' }} onClick={permissionGuide.onAction}>
                                    {permissionGuide.action}
                                </AppButton>}
                                {permissionGuide.instruction && <Box sx={{ mt: 1 }}>
                                    <Typography sx={{ fontSize: '0.85rem', color: '#3c424a', lineHeight: 1.6, wordBreak: 'keep-all' }}>
                                        {permissionGuide.instruction}
                                    </Typography>
                                    {permissionGuide.returnHint && <Typography sx={{ fontSize: '0.8rem', color: '#636a73', lineHeight: 1.6, wordBreak: 'keep-all' }}>
                                        {permissionGuide.returnHint}
                                    </Typography>}
                                </Box>}
                            </Box>}
                        </Box>
                    )}
                    <Collapse in={canAdjustVolume} timeout={180} unmountOnExit>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.15 }}>
                        <SingleLineText sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a1a' }}>{localize("알림 볼륨")}</SingleLineText>
                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: '#5a4a00' }}>
                            {form.alarmVolume}
                        </Typography>
                    </Box>
                    <Box
                        onTouchStart={rememberVolumeTouchStart}
                        onTouchEnd={resetVolumeTouch}
                        onTouchCancel={resetVolumeTouch}
                        onPointerDown={(e) => {
                            if (e.pointerType === 'touch') rememberVolumeTouchStart(e);
                        }}
                        onPointerUp={(e) => {
                            if (e.pointerType === 'touch') resetVolumeTouch();
                        }}
                        onPointerCancel={(e) => {
                            if (e.pointerType === 'touch') resetVolumeTouch();
                        }}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.7 }}
                    >
                        <VolumeDownIcon sx={{ color: '#777', fontSize: 18 }} />
                        <Slider
                            value={form.alarmVolume}
                            onChange={(event, val) => {
                                if (!shouldApplyVolumeChange(event)) return;
                                setForm(prev => ({ ...prev, alarmVolume: val }));
                            }}
                            onChangeCommitted={(event, val) => {
                                if (shouldApplyVolumeChange(event)) {
                                    setForm(prev => ({ ...prev, alarmVolume: val }));
                                }
                                resetVolumeTouch();
                            }}
                            min={0} max={100} step={10}
                            valueLabelDisplay="off"
                            sx={{
                                color: '#8a7000',
                                touchAction: 'pan-y',
                                '& .MuiSlider-thumb': { width: 22, height: 22, bgcolor: 'white', border: '2px solid #ffe566' },
                                '& .MuiSlider-track': { height: 5, borderRadius: 3 },
                                '& .MuiSlider-rail': { height: 5, borderRadius: 3, bgcolor: '#e5e7eb' },
                            }}
                        />
                        <VolumeUpIcon sx={{ color: '#777', fontSize: 18 }} />
                    </Box>
                    </Collapse>
                    <Button
                        disabled={nativePlatform === 'ios' && supportsIosMode && (!iosMode || modeBusy || iosMode.pending || iosMode.legacy)}
                        fullWidth
                        startIcon={testing ? <StopIcon /> : <MusicNoteIcon />}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={testAlarm}
                        sx={{
                            bgcolor: '#fff', color: testing ? '#ef4444' : '#5a4a00',
                            border: testing ? '1px solid #ef4444' : '1px solid #e5e7eb',
                            borderRadius: 3, py: 1.15, fontWeight: 700, fontSize: '0.95rem',
                            '&:hover': { bgcolor: '#f9fafb' },
                        }}
                    >
                        {testing ? localize("정지") : localize("벨소리 테스트")}
                    </Button>
                </Box>
            )}

            <FeatureUpdateDialog platform={nativePlatform} open={updateOpen && (needsVibrationUpdate || needsIosModeUpdate)} onClose={() => setUpdateOpen(false)} />

            <AppToast
                open={!!feedback}
                autoHideDuration={6000}
                onClose={() => setFeedback(null)}
                severity="warning"
                message={localizeSystemError(feedback)}
            />
        </Box>
    );
}
